import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";

import {
  PRODUCT_LOAD_FAILED_TRIGGER,
  pickableProducts,
  productService,
  ProductPickSheet,
} from "@/entities/product";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { productFactsText } from "../../model/dhBlueprint";

/**
 * 「选我的产品 / 按公式生成脚本」弹层(2026-08-10 用户点名返工;2026-10-10 加 lines 变体)。
 *
 * <p>两种用法:
 * <ul>
 *   <li>`formula`:无口播蓝图(或逐句分析拿不到)→ 按「口播公式 × 产品事实」整段生成(blueprint-script)。
 *   <li>`lines`:逐句对照编辑区「选产品」→ 产品事实交给 C2 逐句改写;原话句一字不动。
 * </ul>
 *
 * <p>此前按钮是**闭着眼生成**:`blueprint-script` 的 `userInput`(=产品事实)恒传空串,
 * 而 ai 侧 prompt 明令「输出内容只允许来自用户产品事实」—— 空手让它写,产品库空时
 * 就只能编一个产品出来(真跑编过「栅栏防拆押金」)。
 *
 * <p>现在生成前必须给产品事实:从产品树选(逐字带出 名称/卖点/参数/说明,可改),
 * 或产品库空时手填一句;**没有事实不许生成**。会覆盖已改口播时弹层内明示。
 */
export function DhAdaptProductDialog({
  open,
  onOpenChange,
  variant,
  generating,
  scriptDirty,
  onGenerate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** formula=整段按公式生成;lines=给逐句改写选产品。 */
  variant: "formula" | "lines";
  generating: boolean;
  /** 当前口播文案有用户改动(生成会覆盖,要明示)。 */
  scriptDirty: boolean;
  /** productName:从产品树选的产品名(手填 → null,由调用方从事实里取)。 */
  onGenerate: (facts: string, productName: string | null) => void;
}) {
  const [pickedCode, setPickedCode] = useState<string | undefined>(undefined);
  const [pickedName, setPickedName] = useState<string | null>(null);
  const [facts, setFacts] = useState("");

  // 弹层开着才拉产品树;拉不到不阻断 —— 手填仍是完整可用的路径(与海报/长图同口径)
  const {
    data: nodes = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["tenant-products"],
    queryFn: () => productService.list(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: open,
  });
  const products = pickableProducts(nodes);

  const isLines = variant === "lines";
  const title = isLines ? "选我的产品" : "按公式生成脚本";

  return (
    <Dialog open={open} onOpenChange={(v) => !generating && onOpenChange(v)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isLines
              ? "产品事实只用来替换参考片的品牌/产品名、改写你选了「套句式 / 重写」的句子;原话句一字不动。"
              : "按「口播公式 × 你的产品事实」生成口播脚本。"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-sm">选产品(名称 / 卖点 / 参数逐字带入,可改)</Label>
            {/* 0926 修复波 P11:读取失败不能落进「产品库还没有在售产品」—— 走抽屉的失败态(带重试) */}
            {products.length > 0 || isLoading || isError ? (
              <ProductPickSheet
                products={products}
                allNodes={nodes}
                pickedCode={pickedCode}
                loading={isLoading}
                error={isError}
                onRetry={() => void refetch()}
                triggerLabel={
                  isError && products.length === 0 ? PRODUCT_LOAD_FAILED_TRIGGER : undefined
                }
                disabled={generating}
                onPick={(n) => {
                  setPickedCode(n?.code);
                  setPickedName(n?.name ?? null);
                  // 选中即覆盖事实框(换产品=换一份事实);清除选择不清手填内容
                  if (n) setFacts(productFactsText(n));
                }}
              />
            ) : (
              <p className="rounded-md border border-dashed border-border p-2.5 text-xs text-muted-foreground">
                产品库还没有在售产品 —— 在下面手填一句也行(产品名 + 核心卖点);
                以后在「产品库」建好产品,这里就能直接选。
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dh-adapt-facts" className="text-sm">
              产品事实(生成只会用这里写的,不编造)
            </Label>
            <Textarea
              id="dh-adapt-facts"
              value={facts}
              onChange={(e) => setFacts(e.target.value)}
              disabled={generating}
              rows={5}
              placeholder={"产品:XX净水器\n卖点:三层过滤;免安装\n参数:滤芯寿命 12 个月"}
            />
          </div>

          {scriptDirty && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-xs text-destructive">
              当前口播文案有你改过的内容,生成会<b>整段覆盖</b>;想留底先把它复制走。
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={generating} onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            data-testid="dh-adapt-generate"
            disabled={generating || !facts.trim()}
            onClick={() => onGenerate(facts.trim(), pickedName)}
          >
            {generating ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-1.5 h-4 w-4" />
            )}
            {generating ? "生成中…" : isLines ? "用这个产品" : "生成口播脚本"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
